import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { formatByLocale } from 'utils/date';
import { FormControlLabel, IconButton, Switch } from '@mui/material';
import TextField from '@mui/material/TextField';
import { FontAwesomeSvgIcon } from 'components/shared';
import RecurrenceRule from 'components/shared/RecurrenceRule';

type props = {
  type: 'PUBLISHED' | 'VISIBLE';
  setRecurrenceRule: any;
  recurrenceRule: any;
  cycleWindow: number;
  setCycleWindow: any;
  rounds: any;
  updateRound: any;
  deleteRound: any;
  addRound: any;
  rosterCycles: any;
};

export default function PlanningCycleDialogContent({
  type,
  setRecurrenceRule = null,
  recurrenceRule,
  cycleWindow,
  setCycleWindow,
  rounds,
  updateRound,
  deleteRound,
  addRound,
  rosterCycles
}: props) {
  const { t } = useTranslation('common');

  return (
    <div className="d-flex">
      <div className="settings">
        <RecurrenceRule
          onRecurrenceRuleChange={setRecurrenceRule}
          recurrenceRule={recurrenceRule}
        />

        {recurrenceRule && (
          <div className="padding-bottom">
            <h4>{t('planningsWindow')}</h4>
            <div className="d-flex v-center">
              <div className="heading">
                {t(type === 'PUBLISHED' ? 'publishes' : 'visible')}
              </div>
              <TextField
                value={cycleWindow}
                type="number"
                variant="standard"
                className="inline-input"
                onChange={(e) => setCycleWindow(parseInt(e.target.value, 10))}
                inputProps={{ min: 0 }}
              />
              {recurrenceRule?.type && (
                <div>
                  {t(
                    `${recurrenceRule.type.toLowerCase()}${
                      cycleWindow === 1 ? '' : 's'
                    }`
                  ).toLowerCase()}{' '}
                  {t('inAdvance')}
                </div>
              )}
            </div>
          </div>
        )}

        {recurrenceRule && (
          <div className="padding-bottom">
            <h4>{t('rounds')}</h4>
            {rounds.map((round, roundIndex) => (
              <div key={roundIndex}>
                <div className="d-flex">{`${t('round')} ${
                  roundIndex + 1
                }:`}</div>
                <div className="d-flex v-center">
                  <div className="heading">{`${t('from')} ${t(
                    'day'
                  ).toLowerCase()}`}</div>
                  <TextField
                    value={round.startIndex}
                    type="number"
                    variant="standard"
                    className="inline-input"
                    onChange={(e) =>
                      updateRound(roundIndex, {
                        ...round,
                        startIndex: parseInt(e.target.value, 10)
                      })
                    }
                    inputProps={{ min: 1 }}
                  />
                  <div className="heading">{`${t('until')} ${t(
                    'day'
                  ).toLowerCase()}`}</div>
                  <TextField
                    value={round.endIndex}
                    type="number"
                    variant="standard"
                    className="inline-input"
                    onChange={(e) =>
                      updateRound(roundIndex, {
                        ...round,
                        endIndex: parseInt(e.target.value, 10)
                      })
                    }
                    inputProps={{ min: 1 }}
                  />
                  <FormControlLabel
                    label={t(`autoConnect`)}
                    control={
                      <Switch
                        checked={round.autoConnect}
                        onChange={(e) =>
                          updateRound(roundIndex, {
                            ...round,
                            autoConnect: e.target.checked
                          })
                        }
                      />
                    }
                  />
                  <IconButton
                    onClick={() => deleteRound(roundIndex)}
                    color="error"
                    size="small"
                  >
                    <FontAwesomeSvgIcon icon={faTrashAlt} fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
            <IconButton onClick={() => addRound()} color="primary" size="small">
              <FontAwesomeSvgIcon icon={faPlus} fontSize="small" />
            </IconButton>
          </div>
        )}
      </div>

      {rosterCycles && (
        <div className="summary">
          <h4>{t('examples')}</h4>
          {rosterCycles.map(({ cycleDate, nextOccurrence, rounds }) => (
            <div key={nextOccurrence}>
              <div className="d-flex v-center">
                {t(type === 'PUBLISHED' ? 'publishes' : 'visible')}
                &nbsp; {t('until')} &nbsp;
                <b>{formatByLocale(new Date(cycleDate), 'P')}</b>
                &nbsp; {t('on')} &nbsp;
                <i>{formatByLocale(new Date(nextOccurrence), 'P')}</i>
              </div>
              {rounds &&
                rounds.map(({ start, end, autoConnect }, index) => (
                  <div className="d-flex v-center" key={index}>
                    &nbsp; <b>{`${t('round')} ${index + 1}:`}</b> &nbsp;
                    {t('from')} &nbsp;{' '}
                    <i>{formatByLocale(new Date(start), 'P')}</i>
                    &nbsp; {t('until')} &nbsp;{' '}
                    <i>{formatByLocale(new Date(end), 'P')}</i>
                    &nbsp; {autoConnect && `${t('withAutoConnect')}`}
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
